import straivLogoLight from "@/assets/straiv-logo-light.png";
import straivLogoFallback from "@/assets/straiv-logo.png";
import { PrivacyPolicyContent } from "@/components/PrivacyPolicyContent";
import { TermsContent } from "@/components/TermsContent";
import { useState, useCallback, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";
import { SAFE_AREA } from "@/utils/safeArea";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { Mail, Eye, EyeOff, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Analytics } from "@/lib/analytics";

export const Auth = () => {
  const { user, loading: authLoading, signUp, signIn, signInWithGoogle, signInWithApple, verifyOtp } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [logoSrc, setLogoSrc] = useState(straivLogoLight);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [showPrivacyDialog, setShowPrivacyDialog] = useState(false);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");

  const isIOS = Capacitor.getPlatform() === 'ios';
  const isNative = Capacitor.isNativePlatform();
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  // Listen for keyboard show/hide on native - add padding so inputs can scroll above keyboard
  useEffect(() => {
    if (!isNative) return;
    const listeners: { remove: () => Promise<void> }[] = [];
    const showEvent = isIOS ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent = isIOS ? 'keyboardWillHide' : 'keyboardDidHide';
    let cancelled = false;
    void (async () => {
      const [show, hide] = await Promise.all([
        Keyboard.addListener(showEvent as 'keyboardWillShow', (info) => {
          if (!cancelled) setKeyboardHeight(info.keyboardHeight);
        }),
        Keyboard.addListener(hideEvent as 'keyboardWillHide', () => {
          if (!cancelled) setKeyboardHeight(0);
        }),
      ]);
      if (!cancelled) {
        listeners.push(show, hide);
      } else {
        show.remove();
        hide.remove();
      }
    })();
    return () => {
      cancelled = true;
      listeners.forEach((l) => l.remove());
    };
  }, [isNative, isIOS]);

  // Scroll input into view when focused - never let keyboard cut off inputs
  const handleInputFocus = useCallback((e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = e.target;
    const delay = isIOS ? 450 : 350;
    setTimeout(() => {
      if (document.activeElement === target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, delay);
  }, [isIOS]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-bg flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Redirect as soon as we have a session — do not wait for profile (that caused a stuck login screen).
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast({
        title: t('auth.errors.generic'),
        description: t('auth.errors.passwordsDontMatch'),
        variant: "destructive",
      });
      return;
    }

    if (!termsAccepted) {
      toast({
        title: t('auth.errors.generic'),
        description: t('auth.errors.termsRequired'),
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    localStorage.removeItem('onboarding_completed');

    const { error } = await signUp(email, password);

    if (error) {
      toast({
        title: t('auth.errors.signUpFailed'),
        description: error.message,
        variant: "destructive",
      });
      setLoading(false);
    } else {
      setPendingEmail(email);
      setShowVerificationDialog(true);
      setLoading(false);
      toast({
        title: t('auth.errors.checkEmailTitle'),
        description: t('auth.errors.checkEmailDescription'),
      });
    }
  };

  const handleVerifyCode = async () => {
    if (verificationCode.length !== 6) {
      toast({
        title: t('auth.errors.invalidCode'),
        description: t('auth.errors.invalidCodeDescription'),
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    const { error } = await verifyOtp(pendingEmail, verificationCode);

    if (error) {
      toast({
        title: t('auth.errors.verificationFailed'),
        description: error.message,
        variant: "destructive",
      });
      setLoading(false);
    } else {
      setShowVerificationDialog(false);
      setVerificationCode("");
      setLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const { error } = await signIn(email, password);

    if (error) {
      toast({
        title: t('auth.errors.signInFailed'),
        description: error.message,
        variant: "destructive",
      });
    }
    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    const result = await signInWithGoogle();
    if (result.error) {
      toast({
        title: t('auth.errors.googleSignInFailed'),
        description: result.error.message,
        variant: "destructive",
      });
    }
    setLoading(false);
  };

  const handleAppleSignIn = async () => {
    setLoading(true);
    const result = await signInWithApple();
    if (result.error) {
      toast({
        title: t('auth.errors.appleSignInFailed'),
        description: result.error.message,
        variant: "destructive",
      });
    }
    setLoading(false);
  };

  return (
    <div 
      className="min-h-screen bg-gradient-bg flex flex-col items-center justify-center p-4 overflow-y-auto"
      style={{
        paddingTop: `calc(${SAFE_AREA.top} + 20px)`,
        paddingBottom: keyboardHeight > 0 
          ? `calc(${SAFE_AREA.bottom} + 20px + ${keyboardHeight}px)` 
          : `calc(${SAFE_AREA.bottom} + 20px)`,
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <Card className="w-full max-w-md bg-gradient-card border-0">
        <CardHeader className="text-center space-y-2 pb-2">
          <div className="flex items-center justify-center">
            <span className="text-lg font-medium text-muted-foreground mr-2">{t('auth.welcomeTo')}</span>
            <img
              src={logoSrc}
              alt="Straiv"
              className="h-20 w-auto object-contain max-w-[220px]"
              onError={() => {
                if (logoSrc !== straivLogoFallback) setLogoSrc(straivLogoFallback);
              }}
            />
          </div>
          <CardDescription>
            {t('auth.tagline')}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-2 pb-4">
          {/* Google Sign-In Button */}
          <Button
            variant="outline"
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="w-full border-border/50 hover:bg-secondary/10 mb-3"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            )}
            {t('auth.signInWithGoogle')}
          </Button>

          {/* Apple Sign-In Button - Only show on iOS native app */}
          {isIOS && (
            <Button
              variant="outline"
              onClick={handleAppleSignIn}
              disabled={loading}
              className="w-full border-border/50 hover:bg-secondary/10 mb-4"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
              )}
              {t('auth.signInWithApple')}
            </Button>
          )}

          <div className="relative mb-4">
            <div className="absolute inset-0 flex items-center">
              <Separator />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-gradient-card px-2 text-muted-foreground">
                {t('auth.orContinueWithEmail')}
              </span>
            </div>
          </div>

          <Tabs defaultValue="signin" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signin">{t('auth.signIn')}</TabsTrigger>
              <TabsTrigger value="signup">{t('auth.signUp')}</TabsTrigger>
            </TabsList>
            
            <TabsContent value="signin" className="space-y-3">
              <form onSubmit={handleSignIn} className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="signin-email">{t('auth.email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="signin-email"
                      type="email"
                      placeholder={t('auth.emailPlaceholder')}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onFocus={handleInputFocus}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="signin-password">{t('auth.password')}</Label>
                  <div className="relative">
                    <Input
                      id="signin-password"
                      type={showPassword ? "text" : "password"}
                      placeholder={t('auth.passwordPlaceholder')}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onFocus={handleInputFocus}
                      className="pr-10"
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                </div>
                
                <Button
                  type="submit"
                  className="w-full bg-gradient-primary text-black shadow-glow"
                  disabled={loading}
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  {t('auth.signIn')}
                </Button>
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="link"
                    className="px-0 text-primary"
                    onClick={async () => {
                      if (!email) {
                        toast({ title: t('auth.errors.enterEmailFirst'), description: t('auth.errors.enterEmailFirstDescription') });
                        return;
                      }
                      const { error } = await supabase.auth.resetPasswordForEmail(email, {
                        redirectTo: `${window.location.origin}/reset-password`,
                      });
                      if (error) {
                        toast({ title: t('auth.errors.resetEmailFailed'), description: error.message, variant: "destructive" });
                      } else {
                        Analytics.passwordResetRequested();
                        toast({ title: t('auth.errors.resetEmailSent'), description: t('auth.errors.resetEmailSentDescription') });
                      }
                    }}
                  >
                    {t('auth.forgotPassword')}
                  </Button>
                </div>
               </form>
             </TabsContent>

            <TabsContent value="signup" className="space-y-3">
              <form onSubmit={handleSignUp} className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="signup-email">{t('auth.email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder={t('auth.emailPlaceholder')}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onFocus={handleInputFocus}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="signup-password">{t('auth.password')}</Label>
                  <div className="relative">
                    <Input
                      id="signup-password"
                      type={showPassword ? "text" : "password"}
                      placeholder={t('auth.createPasswordPlaceholder')}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onFocus={handleInputFocus}
                      className="pr-10"
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">{t('auth.confirmPassword')}</Label>
                  <Input
                    id="confirm-password"
                    type={showPassword ? "text" : "password"}
                    placeholder={t('auth.confirmPasswordPlaceholder')}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onFocus={handleInputFocus}
                    required
                  />
                </div>

                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="terms"
                    checked={termsAccepted}
                    onCheckedChange={(checked) => setTermsAccepted(checked as boolean)}
                    required
                  />
                  <Label htmlFor="terms" className="text-sm leading-relaxed">
                    {t('auth.acceptTerms')}{" "}
                    <button
                      type="button"
                      onClick={() => setShowTermsDialog(true)}
                      className="text-primary hover:underline"
                    >
                      {t('auth.termsAndConditions')}
                    </button>{" "}
                    {t('auth.andConjunction')}{" "}
                    <button
                      type="button"
                      onClick={() => setShowPrivacyDialog(true)}
                      className="text-primary hover:underline"
                    >
                      {t('auth.privacyPolicy')}
                    </button>
                  </Label>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-primary text-black shadow-glow"
                  disabled={loading || !termsAccepted}
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  {t('auth.signUp')}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="mt-6 text-center">
        <a
          href="/masterpromptab"
          className="text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors"
        >
          Legal
        </a>
      </div>

      {/* Terms and Conditions Dialog */}
      <Dialog open={showTermsDialog} onOpenChange={setShowTermsDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>{t('auth.termsDialogTitle')}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[60vh] pr-4">
            <TermsContent compact />
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Privacy Policy Dialog */}
      <Dialog open={showPrivacyDialog} onOpenChange={setShowPrivacyDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>{t('auth.privacyDialogTitle')}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[60vh] pr-4">
            <PrivacyPolicyContent compact />
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Email Verification Dialog - position just above number pad when keyboard visible */}
      <Dialog open={showVerificationDialog} onOpenChange={setShowVerificationDialog}>
        <DialogContent
          className="sm:max-w-md"
          style={
            isNative && keyboardHeight > 0
              ? {
                  position: "fixed",
                  top: "auto",
                  left: "50%",
                  bottom: `${keyboardHeight + 16}px`,
                  transform: "translateX(-50%)",
                  transition: "bottom 0.2s ease-out",
                }
              : undefined
          }
        >
          <DialogHeader>
            <DialogTitle className="text-center text-xl">{t('auth.verifyEmailTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="text-center space-y-2">
              <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Mail className="h-8 w-8 text-primary" />
              </div>
              <p className="text-sm text-muted-foreground">
                {t('auth.verifyEmailSentTo')}
              </p>
              <p className="font-semibold text-foreground">{pendingEmail}</p>
              <p className="text-xs text-muted-foreground pt-2">
                {t('auth.verifyEmailCheckInbox')}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="verification-code">{t('auth.verificationCode')}</Label>
              <Input
                id="verification-code"
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                onFocus={handleInputFocus}
                className="text-center text-2xl tracking-widest font-mono"
                required
              />
            </div>

            <Button
              onClick={handleVerifyCode}
              className="w-full bg-gradient-primary text-black shadow-glow"
              disabled={loading || verificationCode.length !== 6}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              {t('auth.verifyEmailButton')}
            </Button>

            <div className="text-center">
              <Button
                type="button"
                variant="link"
                className="text-sm text-muted-foreground"
                onClick={async () => {
                  setLoading(true);
                  const { error } = await signUp(pendingEmail, password);
                  if (error) {
                    toast({
                      title: t('auth.errors.resendFailed'),
                      description: error.message,
                      variant: "destructive",
                    });
                  } else {
                    toast({
                      title: t('auth.errors.codeResent'),
                      description: t('auth.errors.codeResentDescription'),
                    });
                  }
                  setLoading(false);
                }}
              >
                {t('auth.didntReceiveCode')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};